import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, Crown, ShieldCheck, Sparkles, Clock, Hammer } from "lucide-react";
import localApi from "@/lib/localApi";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PERKS = [
  "Unlock full client contact details",
  "Claim unlimited jobs on the Job Board",
  "AI diagnostics & labor estimates",
  "Vehicle health reports & PDF invoices",
  "Email alerts for new nearby jobs",
  "Parts catalog & cost tracking",
];

export default function Billing() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await localApi.get("/marketplace/subscription"); setSub(data); }
    finally { setLoading(false); }
  }, []);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setVerifying(false); toast.error("Still processing — refresh in a moment."); return; }
    try {
      const { data } = await localApi.get(`/marketplace/checkout/status/${sessionId}`);
      if (data.status === "complete") {
        setVerifying(false);
        toast.success("You're in! Your 14-day free trial has started.");
        navigate("/dashboard");
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Checkout session expired."); return; }
    } catch (e) { console.debug("checkout status poll retry", e?.message); }
    setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
  }, [navigate]);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) { setVerifying(true); window.history.replaceState(null, "", "/billing"); pollStatus(sid); }
    load();
  }, [load, pollStatus]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await localApi.post("/marketplace/checkout", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep Pro access until the end of your current period.")) return;
    setBusy(true);
    try {
      await localApi.post("/marketplace/subscription/cancel");
      toast.success("Your subscription will cancel at the end of the period.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(false); }
  };

  const manageBilling = async () => {
    setBusy(true);
    try {
      const { data } = await localApi.post("/marketplace/billing-portal", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open billing portal"); setBusy(false); }
  };

  if (loading) return <Layout title="Pitcrew Pro"><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></Layout>;

  const complimentary = sub?.complimentary;
  const active = sub?.status === "active";
  const trialing = sub?.status === "trialing";
  const entitled = sub?.entitled;

  return (
    <Layout title="Pitcrew Pro Mechanic">
      <div className="max-w-3xl space-y-6 animate-fade-up">
        {!sub?.is_mechanic && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 flex items-center gap-3" data-testid="not-mechanic-banner">
            <Hammer className="h-5 w-5 text-amber-400" />
            <p className="text-sm text-slate-300 flex-1">Enable marketplace availability in Settings to start your free trial and receive jobs.</p>
            <Button data-testid="billing-goto-settings" onClick={() => navigate("/settings")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Settings</Button>
          </div>
        )}

        <div className={`rounded-xl border p-6 ${entitled ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-800 bg-slate-900"}`} data-testid="subscription-status">
          <div className="flex items-center gap-4">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${entitled ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400"}`}>
              {complimentary ? <Crown className="h-6 w-6" /> : active ? <ShieldCheck className="h-6 w-6" /> : trialing ? <Clock className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
            </div>
            <div className="flex-1">
              <p className="font-head font-bold text-lg text-slate-50" data-testid="plan-status-label">
                {complimentary ? "Pitcrew Pro — Complimentary" : active ? "Pitcrew Pro — Active" : trialing ? `Free Trial · ${sub?.days_left} days left` : sub?.status === "expired" ? "Trial ended" : "Not started"}
              </p>
              <p className="text-sm text-slate-400">
                {complimentary ? "You have complimentary Pro access — enjoy! No card needed." : active ? `Renews in ${sub?.days_left} days` : trialing ? "Full access during your 14-day trial" : "Subscribe to unlock leads, claiming and AI tools."}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-amber-500/40 bg-slate-900 p-6 relative">
          <div className="flex items-center gap-2 mb-1"><Crown className="h-5 w-5 text-amber-400" /><p className="font-head font-bold text-slate-100">Pitcrew Pro Mechanic</p></div>
          <p className="font-head font-black text-4xl text-slate-50">{complimentary ? "Free" : <>${sub?.price?.toFixed(2)}<span className="text-base font-normal text-slate-500">/month</span></>}</p>
          <p className="text-xs text-slate-500 mt-1">{complimentary ? "Complimentary Pro access on your account — no card, no charges, ever." : `Enter your card to start a ${sub?.trial_days}-day free trial · you're not charged until the trial ends · cancel anytime`}</p>
          <ul className="mt-5 space-y-2">
            {PERKS.map((p) => (<li key={p} className="flex items-center gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> {p}</li>))}
          </ul>
          {complimentary ? (
            <div className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="complimentary-note">
              <Crown className="h-5 w-5 text-amber-400 shrink-0" />
              <p className="text-sm text-slate-300">All Pro features are unlocked on your account. Nothing to pay — you're all set.</p>
            </div>
          ) : entitled ? (
            <div className="mt-6 space-y-2">
              <Button data-testid="manage-billing-button" onClick={manageBilling} disabled={busy}
                className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Manage billing
              </Button>
              <p className="text-xs text-slate-500 text-center">Update your card, view invoices or cancel via the secure Stripe portal.</p>
              {sub?.cancel_at_period_end ? (
                <div data-testid="cancel-scheduled" className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-400">
                  Your subscription is set to cancel on {sub?.subscribed_until ? new Date(sub.subscribed_until).toLocaleDateString() : "period end"}. You keep access until then.
                </div>
              ) : (
                <Button data-testid="cancel-button" onClick={cancel} disabled={busy}
                  variant="outline" className="w-full h-10 bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800">
                  Cancel subscription
                </Button>
              )}
            </div>
          ) : (
            <Button data-testid="subscribe-button" onClick={subscribe} disabled={busy || verifying}
              className="w-full mt-6 h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {(busy || verifying) && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {verifying ? "Verifying…" : "Start 14-day free trial"}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-600">Secure, encrypted payments processed via Stripe. Your card will not be charged until your 14-day free trial ends.</p>
      </div>
    </Layout>
  );
}
